import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    useMemo,
    useEffect,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { startOf, endOf } from '@/utils/function'

// Default last 7 days range
export const getLast7DaysRange = () => {
    const now = dayjs()
    const sevenDaysAgo = now.subtract(7, 'day')
    return {
        gte: startOf(sevenDaysAgo, 'day'),
        lte: endOf(now, 'day'),
    }
}

export interface LeadFilters {
    contact_source_channel: string[]
    contact_source_status: string
    contact_source_interested_services: string[]
    contact_source_assignee_ids: number[]
    contact_source_created_at_gte: string
    contact_source_created_at_lte: string
    website_referrer: string
    q: string
    assignment_status: string
}

// Filters to encode in base64 (excluding q)
type EncodableFilters = Omit<LeadFilters, 'q'>

export const DEFAULT_FILTERS: LeadFilters = {
    contact_source_channel: [],
    contact_source_status: '',
    contact_source_interested_services: [],
    contact_source_assignee_ids: [],
    contact_source_created_at_gte: '',
    contact_source_created_at_lte: '',
    website_referrer: '',
    q: '',
    assignment_status: '',
}

// Encoded filter format (date as [start, end] array for backward compat)
interface EncodedFilters {
    contact_source_channel?: string[]
    contact_source_status?: string
    contact_source_interested_services?: string[]
    contact_source_assignee_ids?: number[]
    contact_source_created_at?: [string, string] // [gte, lte]
    website_referrer?: string
    assignment_status?: string
}

// Base64 encode/decode helpers - exported for use in statistic queries
export const encodeFilters = (filters: EncodableFilters): string => {
    const encoded: EncodedFilters = {}

    if (filters.contact_source_channel.length > 0)
        encoded.contact_source_channel = filters.contact_source_channel
    if (filters.contact_source_status)
        encoded.contact_source_status = filters.contact_source_status
    if (filters.contact_source_interested_services.length > 0)
        encoded.contact_source_interested_services =
            filters.contact_source_interested_services
    if (filters.contact_source_assignee_ids.length > 0)
        encoded.contact_source_assignee_ids =
            filters.contact_source_assignee_ids
    if (
        filters.contact_source_created_at_gte &&
        filters.contact_source_created_at_lte
    )
        encoded.contact_source_created_at = [
            String(filters.contact_source_created_at_gte),
            String(filters.contact_source_created_at_lte),
        ]
    if (filters.website_referrer)
        encoded.website_referrer = filters.website_referrer
    if (filters.assignment_status)
        encoded.assignment_status = filters.assignment_status

    if (Object.keys(encoded).length === 0) return ''

    return btoa(JSON.stringify(encoded))
}

const decodeFilters = (encoded: string): Partial<EncodableFilters> => {
    if (!encoded) return {}
    try {
        const data: EncodedFilters = JSON.parse(atob(encoded))
        return {
            contact_source_channel: data.contact_source_channel || [],
            contact_source_status: data.contact_source_status || '',
            contact_source_interested_services:
                data.contact_source_interested_services || [],
            contact_source_assignee_ids: data.contact_source_assignee_ids || [],
            contact_source_created_at_gte:
                data.contact_source_created_at?.[0] || '',
            contact_source_created_at_lte:
                data.contact_source_created_at?.[1] || '',
            website_referrer: data.website_referrer || '',
            assignment_status: data.assignment_status || '',
        }
    } catch {
        return {}
    }
}

interface LeadFiltersContextType {
    filters: LeadFilters
    setFilters: (filters: Partial<LeadFilters>) => void
    resetFilters: () => void
}

const LeadFiltersContext = createContext<LeadFiltersContextType | null>(null)

// Params managed externally that should be preserved
const PRESERVED_PARAMS = [
    'tab',
    'sort_by',
    'sort_order',
    'page',
    'page_size',
    'contact_source_id',
]

export const LeadFiltersProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [searchParams, setSearchParams] = useSearchParams()

    // Use refs to avoid callback recreation on every URL change
    const searchParamsRef = useRef(searchParams)
    const setSearchParamsRef = useRef(setSearchParams)
    searchParamsRef.current = searchParams
    setSearchParamsRef.current = setSearchParams

    // Initialize filters from URL params (default 7 days if no date in URL)
    const initialFilters = useMemo((): LeadFilters => {
        const q = searchParams.get('q') || ''
        const filtersParam = searchParams.get('filters') || ''
        const decoded = decodeFilters(filtersParam)
        const defaults = getLast7DaysRange()

        return {
            contact_source_channel: decoded.contact_source_channel || [],
            contact_source_status: decoded.contact_source_status || '',
            contact_source_interested_services:
                decoded.contact_source_interested_services || [],
            contact_source_assignee_ids:
                decoded.contact_source_assignee_ids || [],
            contact_source_created_at_gte:
                decoded.contact_source_created_at_gte || defaults.gte,
            contact_source_created_at_lte:
                decoded.contact_source_created_at_lte || defaults.lte,
            website_referrer: decoded.website_referrer || '',
            q,
            assignment_status: decoded.assignment_status || '',
        }
    }, []) // Only run on mount

    const [filters, setFiltersState] = useState<LeadFilters>(initialFilters)

    // Sync initial default filters to URL on mount (if no filters param in URL)
    useEffect(() => {
        const filtersParam = searchParamsRef.current.get('filters')
        // Only sync if no filters param exists (first visit with defaults)
        if (!filtersParam && initialFilters.contact_source_created_at_gte) {
            const params = new URLSearchParams()
            // Preserve externally-managed params
            PRESERVED_PARAMS.forEach((key) => {
                const value = searchParamsRef.current.get(key)
                if (value) params.set(key, value)
            })
            // Preserve q if exists
            const q = searchParamsRef.current.get('q')
            if (q) params.set('q', q)
            // Encode the default filters (with 7-day date range)
            const { q: _, ...encodableFilters } = initialFilters
            const encoded = encodeFilters(encodableFilters)
            if (encoded) params.set('filters', encoded)
            setSearchParamsRef.current(params, { replace: true })
        }
    }, []) // Only run on mount

    // Stable setFilters callback - uses refs to avoid dependency on searchParams
    const setFilters = useCallback((newFilters: Partial<LeadFilters>) => {
        setFiltersState((prev) => {
            const updated = { ...prev, ...newFilters }

            // Update URL params - preserve DataGrid params
            const params = new URLSearchParams()

            // Preserve externally-managed params
            PRESERVED_PARAMS.forEach((key) => {
                const value = searchParamsRef.current.get(key)
                if (value) params.set(key, value)
            })

            // q stays as separate param
            if (updated.q) params.set('q', updated.q)

            // Encode all other filters into single base64 param
            const { q: _, ...encodableFilters } = updated
            const encoded = encodeFilters(encodableFilters)
            if (encoded) params.set('filters', encoded)

            setSearchParamsRef.current(params)
            return updated
        })
    }, [])

    // Stable resetFilters callback
    const resetFilters = useCallback(() => {
        const defaults = getLast7DaysRange()
        const resetState: LeadFilters = {
            ...DEFAULT_FILTERS,
            contact_source_created_at_gte: defaults.gte,
            contact_source_created_at_lte: defaults.lte,
        }
        setFiltersState(resetState)

        // Preserve externally-managed params and encode default date filter
        const params = new URLSearchParams()
        PRESERVED_PARAMS.forEach((key) => {
            const value = searchParamsRef.current.get(key)
            if (value) params.set(key, value)
        })
        // Encode the 7-day default
        const { q: _, ...encodableFilters } = resetState
        const encoded = encodeFilters(encodableFilters)
        if (encoded) params.set('filters', encoded)
        setSearchParamsRef.current(params)
    }, [])

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({ filters, setFilters, resetFilters }),
        [filters, setFilters, resetFilters]
    )

    return (
        <LeadFiltersContext.Provider value={contextValue}>
            {children}
        </LeadFiltersContext.Provider>
    )
}

export const useLeadFilters = () => {
    const context = useContext(LeadFiltersContext)
    if (!context) {
        throw new Error(
            'useLeadFilters must be used within a LeadFiltersProvider'
        )
    }
    return context
}
